import DirectApi from '../../../directApi';
import Util from '../../../forecast-app/shared/util/util';
import DiscardReportDraftMutation from '../../../mutations/reporting/DiscardReportDraftMutation';
import EnterReportDraftMutation from '../../../mutations/reporting/EnterReportDraftMutation';
import PublishReportMutation from '../../../mutations/reporting/PublishReportMutation';
import SaveReportAsMutation from '../../../mutations/reporting/SaveReportAsMutation';
import UpdateSavedReportMutation from '../../../mutations/reporting/update_saved_report_mutation';
import {ReportDefinitionV1Tasks} from '../contracts/report-definition.types';
import {ReportDataResult} from './reportRowModel';
import {LoadedReport, ReportDataSource, ReportDraftState, ReportLifecycleState, ViewerPermission} from './ReportDataSource';
import {FieldNode, mapFetchResponse} from './reportRowMapper';
import {toReportDefinition} from './storedDefinition';
import {
	buildSaveReportAsInput,
	buildUpdateSavedReportInput,
	SaveReportAsInputFields,
	SavedReportRef,
	UpdateSavedReportInputFields,
} from './savedReportInput';

/**
 * Real `ReportDataSource`. report-service is server-side only (the browser never calls it directly),
 * so this goes through the graphql Node server's thin authenticated passthrough express route
 * `POST /report/data/fetch` (see graphql `server.js` + `ReportServiceLogic.fetchTaskReportBuilderData`),
 * which forwards to report-service and returns the RAW hierarchical wrapped-node JSON that
 * `reportRowMapper` maps into `ReportRow`s.
 *
 * WHY an express passthrough and NOT a Relay field (the codebase's usual preference): the response is
 * a freeform, recursively-nested `{name,value}` blob defined by the report-service taxonomy, not by
 * our schema. Relay-modelling it would need a JSON scalar (loses all typing anyway) or a big
 * recursive type + fragments — real complexity for no benefit over the typed client-side mapper. The
 * route rides the same graphql-server host the browser already hits for `/report/*_export_csv`, so it
 * works in prod (ALB) with no new nginx/terraform routing. Auth is the standard credentialed
 * (cookie session) fetch — same as the export_csv callers.
 */
const FETCH_PATH = 'report/data/fetch';

/** Sibling passthrough returning a saved report's definition (see graphql `server.js`). The saved-report
 * Relay shape carries columns only as a flattened eye-options string, so re-opening a report reads the
 * definition directly rather than rebuilding the field tree in the client. */
const DEFINITION_PATH = 'report/definition';

/** Build the fetch request body from the report definition. `hierarchical: true` is REQUIRED to get the
 * nested / deliverable-tier / context-row response the builder table renders (false/null returns a
 * flat Standard-report array).
 *
 * The definition's `fields` are forwarded verbatim — they are already a concrete `{name, children}`
 * tree (no display-token expansion needed here). Duration columns need the project's estimation unit
 * to render, but the client does NOT request it: report-service guarantees `project.estimationUnit`
 * (+ `minutesPerEstimationPoint`) travels with any hierarchical response that carries a unit-bearing
 * column, so it is always present for the mapper without bloating the definition. */
export function buildFetchBody(
	definition: ReportDefinitionV1Tasks,
	page: {offset: number; limit: number}
): Record<string, unknown> {
	return {
		fields: definition.fields,
		filter: definition.filter,
		pagination: {offset: page.offset, limit: page.limit},
		hierarchical: true,
	};
}

/** The `savedReport` node both saved-report mutations return, narrowed to what a save needs back. */
type SavedReportPayload = {id: string; privateAccess: boolean | null} | null | undefined;

/** Mutation response keyed by the mutation's root field (`saveReportAs` / `updateSavedReport`). */
type SavedReportMutationResponse = Record<string, {savedReport?: SavedReportPayload} | undefined>;

/** A saved-report mutation module (`{commit}`), as `Util.CommitMutation` takes it. */
type SavedReportMutationModule = {commit: unknown};

/** Runs a saved-report mutation as a promise so the caller can await the save and react to a
 * failure.
 *
 * The default `Util.CommitMutation` failure path pops a global mutation-error modal; the report
 * builder reports a failed save on the surface itself and keeps the unsaved draft editable, so the
 * `onError` override deliberately does NOT delegate to that fallback. It also gives up the
 * wrapper's one automatic retry of a transient network failure — the surface's error state invites
 * the user to press Save again instead.
 *
 * A response that resolves without a `savedReport` id is treated as a failure: without an id a
 * follow-up save could not target the report and would silently create a duplicate. */
function commitSave(
	mutation: SavedReportMutationModule,
	input: SaveReportAsInputFields | UpdateSavedReportInputFields,
	selectSavedReport: (response: SavedReportMutationResponse) => SavedReportPayload
): Promise<SavedReportRef> {
	return new Promise<SavedReportRef>((resolve, reject) => {
		Util.CommitMutation(
			mutation,
			input,
			(response: SavedReportMutationResponse) => {
				const savedReport = selectSavedReport(response);
				if (!savedReport?.id) {
					reject(new Error('saved report mutation returned no report'));
					return;
				}
				resolve({id: savedReport.id, privateAccess: savedReport.privateAccess ?? true});
			},
			true,
			null,
			(transaction: unknown) => reject(new Error(String(transaction)))
		);
	});
}

/** The lifecycle payload each mutation returns, keyed by its root field. `draftState` arrives typed
 * as `ReportDraftState | null` by the generated Relay types, but the underlying GraphQL field is a
 * plain `String`, not an enum — report-service is free to add or misspell a value with no schema
 * change, so the raw payload is read as an unnarrowed string here and validated in `narrowDraftState`
 * before it reaches the union type. */
type LifecycleMutationResponse = Record<
	string,
	(Omit<ReportLifecycleState, 'draftState'> & {draftState?: string | null}) | undefined
>;

const KNOWN_DRAFT_STATES: ReadonlySet<string> = new Set(['PUBLISHED', 'DRAFT', 'UNPUBLISHED']);

/** Narrows a raw `draftState` string to the known union, mapping anything else — including a value
 * report-service might add later — to null rather than throwing. Consumers treat a null/unknown
 * state as "no reader isolation proven", the conservative reading: a publish that actually succeeded
 * must not be reported as a failure just because of an unrecognised state string. */
function narrowDraftState(draftState: string | null | undefined): ReportDraftState | null {
	return draftState != null && KNOWN_DRAFT_STATES.has(draftState) ? (draftState as ReportDraftState) : null;
}

/** A lifecycle mutation module (`{commit}`), as `Util.CommitMutation` takes it. */
type LifecycleMutationModule = {commit: unknown};

/** Runs a lifecycle mutation as a promise. Same reasoning as `commitSave`: the surface reports a
 * failed lifecycle action itself and keeps the editor where they are, so the default global
 * mutation-error modal is deliberately not delegated to. A response without a `draftState` and
 * without `locked` is treated as a failure, because the surface would otherwise show a state it
 * never received. */
function commitLifecycle(
	mutation: LifecycleMutationModule,
	reportId: string,
	rootField: string
): Promise<ReportLifecycleState> {
	return new Promise<ReportLifecycleState>((resolve, reject) => {
		Util.CommitMutation(
			mutation,
			{reportId},
			(response: LifecycleMutationResponse) => {
				const payload = response?.[rootField];
				if (!payload || (payload.draftState == null && !payload.locked)) {
					reject(new Error(`${rootField} returned no lifecycle state`));
					return;
				}
				resolve({
					draftState: narrowDraftState(payload.draftState),
					publishedAt: payload.publishedAt ?? null,
					locked: payload.locked === true,
				});
			},
			true,
			null,
			(transaction: unknown) => reject(new Error(String(transaction)))
		);
	});
}

export class HttpReportDataSource implements ReportDataSource {
	async fetchRows(definition: ReportDefinitionV1Tasks, page: {offset: number; limit: number}): Promise<ReportDataResult> {
		const response = await fetch(DirectApi.graphqlServerEndpoint(FETCH_PATH), {
			method: 'POST',
			headers: {'Content-Type': 'application/json'},
			credentials: 'include',
			body: JSON.stringify(buildFetchBody(definition, page)),
		});
		if (!response.ok) {
			throw new Error(`report/data/fetch failed: ${response.status}`);
		}
		const nodes = (await response.json()) as FieldNode[];
		return mapFetchResponse(Array.isArray(nodes) ? nodes : [], page.limit);
	}

	async loadDefinition(savedReportId: string): Promise<LoadedReport> {
		const response = await fetch(DirectApi.graphqlServerEndpoint(`${DEFINITION_PATH}/${savedReportId}`), {
			method: 'GET',
			credentials: 'include',
		});
		if (!response.ok) {
			throw new Error(`report/definition failed: ${response.status}`);
		}
		const stored = (await response.json()) as Record<string, unknown>;
		// Fails closed: an unreadable or absent permission hides edit affordances rather than offering
		// edits the backend will reject.
		const viewerPermission: ViewerPermission =
			stored.viewerPermission === 'OWNER' || stored.viewerPermission === 'EDITOR' ? stored.viewerPermission : 'VIEWER';
		return {
			definition: toReportDefinition(stored),
			// An unreadable or absent origin is treated as a standard report: the surface must not claim
			// an AI origin it cannot prove.
			source: stored.source === 'AI' ? 'AI' : 'STANDARD',
			promptText: typeof stored.promptText === 'string' && stored.promptText !== '' ? stored.promptText : null,
			viewerPermission,
			// Same narrowing the lifecycle mutations use: the stored state is a free-form string on the wire,
			// so anything outside the known union becomes null rather than reaching the union type unchecked.
			draftState: narrowDraftState(typeof stored.draftState === 'string' ? stored.draftState : null),
			publishedAt: typeof stored.publishedAt === 'string' && stored.publishedAt !== '' ? stored.publishedAt : null,
			// Deliberately not defaulted: a non-boolean means the stored value is unknown, and an update
			// overwrites this field unconditionally, so guessing would flip the report between private and
			// shared.
			privateAccess: typeof stored.privateAccess === 'boolean' ? stored.privateAccess : null,
		};
	}

	saveDefinition(
		definition: ReportDefinitionV1Tasks,
		saved: SavedReportRef | null,
		promptText: string | null
	): Promise<SavedReportRef> {
		return saved === null
			? commitSave(
					SaveReportAsMutation,
					buildSaveReportAsInput(definition, promptText),
					response => response?.saveReportAs?.savedReport
			  )
			: commitSave(
					UpdateSavedReportMutation,
					buildUpdateSavedReportInput(saved, definition),
					response => response?.updateSavedReport?.savedReport
			  );
	}

	enterDraft(savedReportId: string): Promise<ReportLifecycleState> {
		return commitLifecycle(EnterReportDraftMutation, savedReportId, 'enterReportDraft');
	}

	publish(savedReportId: string): Promise<ReportLifecycleState> {
		return commitLifecycle(PublishReportMutation, savedReportId, 'publishReport');
	}

	discard(savedReportId: string): Promise<ReportLifecycleState> {
		return commitLifecycle(DiscardReportDraftMutation, savedReportId, 'discardReportDraft');
	}
}
